import { FC, useState } from 'react';
import { CareMapDrawerPage } from '../components/CareMapDrawerPage';
import { DrawerSectionHeading } from '../components/DrawerSectionHeading';
import { CareMapState } from '../caremap/CareMapState';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { IonButton, IonItem, IonLabel, IonText } from '@ionic/react';
import { isPlatform } from '../utils/isPlatform';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { Text } from '../components/Text';
import { MemberAvatar } from './MemberAvatar';
import { ShortContactMethodsList } from '../contact-methods/ShortContactMethodsList';
import { CareMapNotesList } from '../notes/note-list/CareMapNotesList';
import { LabelProvider } from '../config/LabelProvider';
import { DrawerListSection } from '../components/DrawerListSection';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { PrimaryCaregiverBadge } from './PrimaryCaregiverBadge';
import { NudgeBadge } from '../components/NudgeBadge';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { AddFromPopover } from '../contacts/AddFromPopover';
import { UserState } from '../user/UserState';

export const HouseholdMemberList: FC = () => {
    const mixpanel = useInstance(MixpanelService);
    const history = useInstance(HistoryViewModel);

    const userState = useInstance(UserState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const labelProvider = useInstance(LabelProvider);

    const [showAddMemberPopover, setShowAddMemberPopover] = useState<boolean>(false);
    const [showAddMemberPopoverTrigger, setShowAddMemberPopoverTrigger] = useState<string>();

    const caremap = careMapState.getActiveCaremap();

    if (!caremap) {
        return null;
    }

    const patient = caremap.patient;
    const members = caremap.household.members;

    const navigateToAdd = () => {
        history.push(`/caremaps/${caremap?.careMapId}/drawer/household/add-member`);
    };

    const onAdd = (trigger?: string) => {
        if (isPlatform('capacitor')) {
            setShowAddMemberPopover(true);
            setShowAddMemberPopoverTrigger(trigger || 'add-member-btn');
        } else {
            navigateToAdd();
        }
    };

    const viewMoreNotes = () => {
        history.push(`/caremaps/${caremap?.careMapId}/drawer/household/notes`);
    };

    const editNote = (noteId: string) => {
        history.push(`/caremaps/${caremap?.careMapId}/drawer/household/notes/${noteId}`);
    };

    const addNote = () => {
        history.push(`/caremaps/${caremap?.careMapId}/drawer/household/notes/add-note`);
    };

    const innerCircleComplete = () => {
        careMapState.completeOnboardingStep('household-members');
        mixpanel.track('Inner Circle Completed');
    };

    const btnLabel = 'Add Inner Circle Member';
    const buttons = <>
        {caremap?.links.self.actions.includes('add-support') && (
            <IonButton
                id="add-member-btn"
                onClick={() => onAdd()}
                data-type="add-member-button"
            >+ {btnLabel}</IonButton>
        )}
    </>;

    const showBadge = !careMapState.hasCompletedOnboardingStep('household-members');
    const notes = notesFactory.forActiveCaremap().getNotesForTarget({ section: 'household' });
    const livesWith = careMapState.isUserThePatient() ? 'you' : careMapState.getPatientFirstName();

    return (
        <CareMapDrawerPage title="Inner Circle" buttons={buttons}>
            <DrawerListSection underDrawerTitle>
                {showBadge && (
                    <NudgeBadge
                        message={`Add close family members, those who live with ${livesWith}, and anyone else that helps with daily care. This is the core team- the Inner Circle!`}
                        buttons={[{
                            label: btnLabel,
                            id: 'add-member-nudge-btn',
                            'data-type': 'add-inner-circle',
                            clickHandler: () => onAdd('add-member-nudge-btn')
                        },{
                            label: 'I\'m Done Adding',
                            clickHandler: innerCircleComplete,
                            'data-type': 'inner-circle-done'
                        }]}
                    />
                )}
            </DrawerListSection>

            <DrawerListSection>
                <CareMapNotesList
                    showCount={3}
                    onNoteClicked={editNote}
                    onAddNote={addNote}
                    moreNotesHandler={viewMoreNotes}
                    notes={notes}
                />
            </DrawerListSection>

            <div data-type="member-list">
                <div style={{paddingLeft: 5}}>
                    <DrawerSectionHeading text="Inner Circle Members" />
                </div>
                <div>

                    <IonItem
                        button
                        detail={true}
                        onClick={() => history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/household/member/patient`)}
                    >
                        <div slot="start" style={{marginTop: 10, marginBottom: 10}}>
                            <MemberAvatar member={patient} />
                        </div>
                        <IonLabel>
                            <div>
                                <IonText data-type="title" className="mp-mask" style={{fontWeight: 500}}>{patient.firstName} {patient.lastName}</IonText>
                                <Text type="detail" data-type="detail1">{labelProvider.getLabel('CARE_RECIPIENT')}</Text>
                            </div>
                            <ShortContactMethodsList contactMethods={patient.contactMethods} address={patient.address} />
                        </IonLabel>
                    </IonItem>

                    {members.map(member => {
                        return (
                            <IonItem
                                key={member.id}
                                button
                                detail={true}
                                onClick={() => history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/household/member/${member.id}`)}
                            >
                                <div slot="start" style={{marginTop: 10, marginBottom: 10}}>
                                    <MemberAvatar member={member} />
                                </div>
                                <IonLabel>
                                    <div>
                                        <IonText data-type="title" className="mp-mask" style={{fontWeight: 500}}>{member.firstName} {member.lastName}{member.userEmail && member.userEmail === userState.state.email ? ' (You)' : ''}</IonText>
                                        <Text type="detail" data-type="detail1">{member.relationship || ''}</Text>
                                    </div>
                                    <ShortContactMethodsList contactMethods={member.contactMethods} address={member.address} />
                                    <div style={{display: 'flex', marginTop: 4}}>
                                        <PrimaryCaregiverBadge primaryCaregiver={member.primaryCaregiver} />
                                        <div>&nbsp;</div>
                                    </div>
                                </IonLabel>
                            </IonItem>
                        );
                    })}

                </div>
            </div>

            <AddFromPopover
                isOpen={showAddMemberPopover}
                trigger={showAddMemberPopoverTrigger}
                onDidDismiss={() => setShowAddMemberPopover(false)}
                buttonHandlers={{
                    manually: navigateToAdd,
                    fromContacts: () => history.push(`/caremaps/${caremap?.careMapId}/drawer/household/add-member?aafc=true`)
                }}
            />
        </CareMapDrawerPage>
    );
};
